import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Icon } from '../../../../components/common/icon/Icon';

import { useCheckUserLoggedIn } from '../../../../hooks/user/useCheckUserLoggedIn';
import { LoginToCheckPrice } from '../components/LoginToCheckPrice';
import { IMarketPrice } from '../../../../interfaces/MarketPrice.interfaces';
import { formatPrice } from '../../../../utils/formatter/formatPrice.util';
import { marketPricePageInfo } from '../../../../constants/pageInfo/pageInfo.constant';
import { ampli } from '../../../../ampli';

interface TractorValueDetailCardProps {
  product: IMarketPrice;
}

export const TractorValueDetailCard = ({ product }: TractorValueDetailCardProps) => {
  const navigate = useNavigate();
  const { isUserLoggedIn } = useCheckUserLoggedIn();

  const handleClick = () => {
    ampli.pvmClickMarketPriceCard({
      model: product.machine.model,
      engineOutputInPs: product.machine.engineOutputInPs,
      maxPrice: product.maxPrice,
      minPrice: product.minPrice,
    });
    navigate(`${marketPricePageInfo.detailPage.path}/${product.machine.id}`);
  };

  const getTitle = () => {
    return `${product.brand.name} ${product.machine.model} (${product.machine.engineOutputInPs}마력)`;
  };

  return (
    <Wrapper>
      <Container onClick={handleClick}>
        <ImageWrap>
          <Image src={product.machine.imageMain || product.brand.image} loading={'lazy'} />
        </ImageWrap>

        <ProductInfoSection>
          <ProductContentGroup>
            <Title>{getTitle()}</Title>
            <HorizontalLine />
            {isUserLoggedIn && (
              <PriceSection>
                <PriceWrapper>
                  <Icon name={'AngleFillUp'} size={'Medium'} fill={'#FF3049'} />
                  <Price>{formatPrice(product.maxPrice)}원</Price>
                </PriceWrapper>
                <PriceWrapper>
                  <Icon name={'AngleFillDown'} size={'Medium'} fill={'#445EFF'} />
                  <Price>{formatPrice(product.minPrice)}원</Price>
                </PriceWrapper>
              </PriceSection>
            )}
            {!isUserLoggedIn && <LoginToCheckPrice defaultText={'로그인 후 시세 확인'} />}
          </ProductContentGroup>
          <ProductMetaContainer>
            <MetricsGroup>
              {/* TODO: 조회수 카운트 구현 https://gint-team.atlassian.net/browse/PVM-3157 */}
              {/* <ViewCount>
                <Icon name={'Eye'} size={'Medium'} fill={'#C6C6D5'} />
                <Count>{Number(product.views || 0).toLocaleString()}</Count>
              </ViewCount>
              <SplitBarWrapper>
                <SplitBar>{'|'}</SplitBar>
              </SplitBarWrapper> */}
              <InquiryCount>
                <Icon name={'DocumentBill'} size={'Medium'} fill={'#C6C6D5'} />
                <Count>{Number(product.priceCount || 0).toLocaleString()}</Count>
              </InquiryCount>
            </MetricsGroup>
          </ProductMetaContainer>
        </ProductInfoSection>
      </Container>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  padding: 24px 0px;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  align-self: stretch;

  border-bottom: 1px solid var(--Light-Scheme-Monochrome-grey20, #ececf3);

  :last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
`;

const Container = styled.div`
  width: 100%;
  height: 160px;

  display: flex;
  justify-content: flex-start;
  align-items: flex-start;

  align-self: stretch;

  gap: 16px;

  cursor: pointer;
`;

const ImageWrap = styled.div`
  min-width: 150px;
  height: 100%;

  position: relative;

  border-radius: 8px;

  background-color: #f2f3f5;

  padding: 0px 16px;
`;

const Image = styled.img`
  width: 100%;
  height: 100%;

  left: 0;
  top: 0;

  object-fit: cover;

  position: absolute;

  border-radius: 8px;

  opacity: 1;
`;

const ProductInfoSection = styled.div`
  width: 100%;
  height: 100%;

  display: flex;
  padding: 4px 0px;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 4px;

  opacity: 1;
`;

const ProductContentGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  flex: 1 0 0;
  align-self: stretch;
`;

const Title = styled.span`
  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* Display/Large/l-regular */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 130%; /* 20.8px */

  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;

  overflow: hidden;
`;

const HorizontalLine = styled.div`
  width: 100%;
  height: 1px;
  background-color: var(--Light-Scheme-Monochrome-grey20, #ececf3);
`;

const PriceSection = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 4px;
  align-self: stretch;
`;

const PriceWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const Price = styled.span`
  color: var(--Light-Scheme-Monochrome-offblack, #505363);

  /* Display/Medium/m-medium */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 130%; /* 18.2px */
`;

const ProductMetaContainer = styled.div`
  display: flex;
  margin-top: auto;
  align-items: center;
  justify-content: flex-end;
  align-content: center;
  align-self: stretch;
  flex-wrap: wrap;
`;

const MetricsGroup = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  align-self: stretch;
`;

const ViewCount = styled.span`
  display: flex;
  padding: 2px 0px;
  align-items: center;
`;

const InquiryCount = styled.span`
  display: flex;
  padding: 2px 0px;
  align-items: center;
`;

const Count = styled.span`
  color: var(--Light-Scheme-Monochrome-grey100, #858894);
  text-align: center;

  /* System/Small/s-medium */
  font-family: Pretendard;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 85%; /* 10.2px */
`;

const SplitBarWrapper = styled.div`
  display: flex;
  padding: 0px 2px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 10px;
`;

const SplitBar = styled.span`
  color: var(--Light-Scheme-Monochrome-grey20, #ececf3);
  text-align: center;

  /* System/Medium/m-light */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 300;
  line-height: 85%; /* 11.9px */
`;
